<template>
  <div>
    <h2>Adding Getters & Setters to Computed Properties</h2>
    <br/>
    <p>
      Computed properties are getter-only by default, but if you need to, you can also specify a setter. To do this, we actually need to change our computed property from a function to an object with two properties; get and set. These properties are functions that are invoked when we use the computed property to retrieve and update the value, respectively.
    </p>
    <pre>
      <code>computed: {
  fullName: {
		get() {
      
		},
		set(newValue) {

		}
	}
}</code>
    </pre>

    <p>
      The logic to retrieve the property’s value is going to remain the same, so I can simply copy in the code from the old function.
    </p>
    <pre>
      <code>get: function() {
	return this.firstName + ' ' + this.lastName;
}</code>
    </pre>

    <p>
      As for the set function, you might have noticed that I added a newValue parameter. As the name of the parameter suggests, it will contain the new value whenever we assign a new value to the computed property. Since I will be assigning the full name as a whole, I need to do a bit of splitting to accomplish the same behavior as before. I will also add an alert for this function so we can see when it gets called. Then I will simply split the value by space and assign the first part to the firstName data property and the last part to the lastName property.
    </p>
    <pre>
      <code>set: function(newValue) {
	let parts = newValue.split(' ');
	this.firstName = parts[0];
	this.lastName = parts[parts.length - 1];
}</code>
    </pre>

    <p>So within the set function, I simply update the data properties as I did within the other event listener. Let’s now get to actually using these functions. The next thing I will do, is to add a new button which will use the setter that we will implement.</p>
    <p>I will also need to add a new event listener for this button. Now the code looks like this,</p>
    <pre>
      <code>&lt;template&gt;
  &lt;v-card class="pa-5" light&gt;
    &lt;v-row dense&gt;
      &lt;v-col cols="12" sm="6"&gt;
        &lt;h4&gt;Displaying name from the data property&lt;/h4&gt;
        &lt;br/&gt;
        &lt;h3&gt;FirstName - {{ testString3 }} LastName - {{ testString4 }}&lt;/h3&gt;
      &lt;/v-col&gt;
      &lt;v-col cols="12" sm="6"&gt;
        &lt;h4&gt;Displaying name from the SETTER property&lt;/h4&gt;&lt;br/&gt;
        &lt;h3&gt;Full Name - {{ testString2 }}&lt;/h3&gt;
      &lt;/v-col&gt;
    &lt;/v-row&gt;
    &lt;v-row dense&gt;
      &lt;v-col&gt;
        &lt;div class="text-center"&gt;
          &lt;v-btn color="primary" dark v-on:click="changeName" class="mr-5"&gt;Change Name&lt;/v-btn&gt;
          &lt;v-btn color="success" dark v-on:click="changeNameSetter"&gt;Change Name (setter)&lt;/v-btn&gt;
        &lt;/div&gt;
      &lt;/v-col&gt;
    &lt;/v-row&gt;
  &lt;/v-card&gt;
&lt;/template&gt;
&lt;script&gt;
  export default{
    name: "GetterSetter",
    data() {
      return {
       firstName: 'Paul',
      lastName: 'Walker',
    };
  },
  methods: {
    changeName: function() {
      this.firstName = 'Vin';
      this.lastName = 'Diesel';
    },
    changeNameSetter: function() {
    	this.fullName = 'Steve Rogers';
    }
  },
  computed: {
    fullName: {
    	get() {
    		return this.firstName + ' ' + this.lastName;
      },
      set(newValue) {
        var parts = newValue.split(' ');
	      this.firstName = parts[0];
  	    this.lastName = parts[parts.length - 1];
      }
    } 
  }
&lt;/script&gt;</code>
    </pre>
    
    <h2>Output</h2>
    <br />
    <v-card class="pa-5" light>
      <v-row dense>
        <v-col cols="12" sm="6">
          <h4>Displaying name from the data property</h4><br/>
          <h3>FirstName - {{ firstName }}<br/>LastName - {{ lastName}}</h3>
        </v-col>
        <v-col cols="12" sm="6">
          <h4>Displaying name from the SETTER property</h4><br/>
          <h3>Full Name - {{ fullName }}</h3>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <div class="text-center">
            <v-btn color="primary" dark v-on:click="changeName" class="mr-5">Change Name</v-btn>
            <v-btn color="success" dark v-on:click="changeNameSetter">Change Name (setter)</v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>
<script>
export default {
  name: "GetterSetter",
  data() {
    return {
      testString: "{{this.firstName}} {{this.lastName}}",
      testString2: "{{this.fullName}}",
      testString3: "{{this.firstName}}",
      testString4: "{{this.lastName}}",
      firstName: 'Paul',
      lastName: 'Walker',
    };
  },
  methods: {
    changeName: function() {
      this.firstName = 'Vin';
      this.lastName = 'Diesel';
    },
    changeNameSetter: function() {
    	this.fullName = 'Steve Rogers';
    }
  },
  computed: {
    fullName: {
    	get() {
    		return this.firstName + ' ' + this.lastName;
      },
      set(newValue) {
        var parts = newValue.split(' ');
	      this.firstName = parts[0];
  	    this.lastName = parts[parts.length - 1];
      }
    }
  },
};
</script>
